import React, { useState, useEffect, useRef } from 'react';
import { X, Tag as TagIcon, ChevronDown, Search } from 'lucide-react';
import { tagAPI } from '../services/api';
import { tagBadgeClasses } from '../utils/tagColors';
import useDropdownPlacement from '../hooks/useDropdownPlacement';

const PANEL_MAX = 420; // the old max-h-[60vh], give or take
const PANEL_MIN = 220;

/**
 * Multi-select tag picker restricted to admin-managed tags. Tags are now defined under
 * Settings → Tags; the patient form can only assign from that predefined list. No free-form
 * entry — if a tag is missing, the user must add it under Settings first.
 *
 * Contract preserved for backward compatibility with existing patient-form state:
 *   value:    string[] of tag NAMES currently selected
 *   onChange: (newNames: string[]) => void
 *
 * The backend still resolves names → tags case-insensitively, so this keeps the existing API
 * contract intact while changing the UX from "type-to-create" to "pick-from-list".
 */
const TagInput = ({ value = [], onChange, placeholder = 'Pick a tag…', label, max }) => {
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  const selectedNames = Array.isArray(value) ? value : [];
  const limitReached = typeof max === 'number' && selectedNames.length >= max;

  // Load the admin-managed tag list once. Closes on outside click. We always re-fetch on
  // re-open is overkill — the list changes rarely and an extra round-trip per click is worse
  // UX than occasionally seeing a stale list. The user can refresh the page if Settings was
  // just updated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await tagAPI.getAll();
        if (!cancelled) setAllTags(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAllTags([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    // Escape to close — common UX expectation for any popover. Without it the only ways
    // to close were "click outside" or "click the chip strip again", neither obvious when
    // the dropdown is covering the form's Save button below it.
    // Capture phase, stopped: this Escape closes the dropdown and not a dialog the field sits in.
    const onKey = (e) => {
      if (e.key === 'Escape' && open) {
        e.stopPropagation();
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  // Always opens downward. We used to flip upward when there wasn't 280px of viewport
  // below the trigger, but that caused two problems: (a) the threshold tripped on
  // smaller browser windows / higher OS scaling, making the same screen render the
  // dropdown differently on different PCs; (b) flipping up covered the form fields
  // immediately above (Phone, DOB), which read as "broken" to users. The dropdown
  // already has max-h-[60vh] and lives inside a max-h-[95vh] scrollable modal, so
  // the modal handles any vertical overflow consistently across screen sizes.

  const toggle = (name) => {
    const lower = name.toLowerCase();
    const without = selectedNames.filter(n => n.toLowerCase() !== lower);
    if (without.length === selectedNames.length) {
      // Adding a new tag — block if at the max limit.
      if (typeof max === 'number' && selectedNames.length >= max) return;
      onChange([...selectedNames, name]);
    } else {
      onChange(without);
    }
  };

  const removeTag = (name) => {
    onChange(selectedNames.filter(n => n.toLowerCase() !== name.toLowerCase()));
  };

  const q = search.trim().toLowerCase();
  const filteredTags = allTags.filter(t => {
    if (!q) return true;
    return t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
  });
  const selectedSet = new Set(selectedNames.map(n => n.toLowerCase()));

  const { dropUp, space } = useDropdownPlacement(open, wrapperRef, {
    wantHeight: PANEL_MAX,
    minHeight: PANEL_MIN,
  });

  return (
    <div ref={wrapperRef} className="relative">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      {/* Chip strip + open button. Clicking anywhere opens the picker. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full min-h-[44px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border-2 border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
      >
        <TagIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-1" />
        {selectedNames.length === 0 ? (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        ) : (
          selectedNames.map(name => (
            <span
              key={name}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tagBadgeClasses(name)}`}
            >
              {name}
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); removeTag(name); }}
                className="hover:bg-black/10 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </span>
            </span>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-auto mr-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        // Opens below the chip strip, or above it when the space below is clipped away by the
        // modal body. Height is capped to whatever room the chosen side actually has.
        <div
          className={`absolute z-30 w-full bg-white border-2 border-gray-200 rounded-lg shadow-xl flex flex-col
            ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          style={{ maxHeight: Math.max(PANEL_MIN, Math.min(PANEL_MAX, space)) }}
        >
          {/* Sticky header — title + count + close. Replaces the old bottom Done button
              so the user never has to scroll the dropdown to dismiss it. */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-lg flex-shrink-0">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Pick tags {selectedNames.length > 0 && <span className="ml-1 text-gray-500 normal-case font-normal">· {selectedNames.length} selected{typeof max === 'number' ? ` of ${max}` : ''}</span>}
            </span>
            <button
              type="button"
              onClick={() => { setOpen(false); setSearch(''); }}
              className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
              aria-label="Close"
              title="Done (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-2 border-b border-gray-100 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tags…"
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
          </div>
          {limitReached && (
            <div className="px-3 py-2 text-xs bg-amber-50 text-amber-800 border-b border-amber-200 flex-shrink-0">
              Max {max} tags. Remove one to add another.
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-3 text-sm text-gray-500">Loading tags…</div>
            ) : allTags.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No tags defined yet. Add tags under <span className="font-semibold">Settings → Tags</span> first.
              </div>
            ) : filteredTags.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">No tags match "{search}".</div>
            ) : (
              filteredTags.map(t => {
                const checked = selectedSet.has(t.name.toLowerCase());
                // When the cap is hit, unselected rows are dimmed and inert — the
                // user must deselect an existing tag before picking another.
                const disabled = limitReached && !checked;
                return (
                  <label
                    key={t.id}
                    className={`flex items-center gap-2.5 px-3 py-2 transition-colors ${
                      disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : `cursor-pointer ${checked ? 'bg-emerald-50' : 'hover:bg-gray-50'}`
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(t.name)}
                      className="rounded border-gray-300 focus:ring-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{t.name}</div>
                      {t.description && (
                        <div className="text-xs text-gray-500 truncate">{t.description}</div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagInput;
